import { Link } from 'react-router-dom'
import {UserPlus, Building2, Search, Link2, Star, Award, ShieldCheck, ArrowRight, CheckCircle2, GraduationCap, TrendingUp, FileText, ChevronRight, } from 'lucide-react'
import Navbar from '@/components/Navbar'
import Footer from '@/components/Footer'

const STEPS_COLABORADOR = [
  {
    num: '01',
    icon: UserPlus,
    title: 'Crea tu cuenta',
    desc: 'Regístrate con tu correo y elige el tipo de perfil: Colaborador. En segundos tienes acceso a la plataforma.',
  },
  {
    num: '02',
    icon: FileText,
    title: 'Completa tu perfil profesional',
    desc: 'Añade experiencia laboral, habilidades, certificaciones, idiomas, nivel de estudios y pretensión salarial para que las empresas te encuentren.',
  },
  {
    num: '03',
    icon: Search,
    title: 'Explora vacantes y postúlate',
    desc: 'Filtra por ciudad, modalidad, salario y giro empresarial. Envía tu candidatura en un clic con tu perfil Beeznz Work.',
  },
  {
    num: '04',
    icon: Link2,
    title: 'Vinculación laboral',
    desc: 'Al ser aceptado en una empresa, se crea un vínculo oficial dentro de la plataforma que activa tu historial laboral.',
  },
  {
    num: '05',
    icon: Star,
    title: 'Acumula evaluaciones',
    desc: 'Las empresas evalúan tu desempeño mensualmente. Tus calificaciones conforman tu reputación laboral verificable.',
  },
  {
    num: '06',
    icon: Award,
    title: 'Certifícate y crece',
    desc: 'Completa capacitaciones para obtener certificados digitales que se integran automáticamente a tu perfil profesional.',
  },
]

const STEPS_EMPRESA = [
  {
    num: '01',
    icon: Building2,
    title: 'Registra tu empresa',
    desc: 'Crea una cuenta de empresa, completa el perfil corporativo con logotipo, giro, datos de contacto y descripción.',
  },
  {
    num: '02',
    icon: FileText,
    title: 'Publica vacantes',
    desc: 'Define puesto, salario, modalidad, requisitos y fecha de cierre. Tus vacantes aparecen en la bolsa de trabajo pública de Beeznz Work.',
  },
  {
    num: '03',
    icon: Search,
    title: 'Revisa candidatos',
    desc: 'Filtra postulantes, revisa perfiles completos con historial verificable y descarga CVs de los candidatos más alineados.',
  },
  {
    num: '04',
    icon: Link2,
    title: 'Vincula a tus colaboradores',
    desc: 'Envía invitaciones de vínculo laboral a tus empleados. Al aceptar, se formaliza la relación dentro de la plataforma.',
  },
  {
    num: '05',
    icon: Star,
    title: 'Evalúa el desempeño',
    desc: 'Registra evaluaciones mensuales por área y colaborador con los 10 aspectos estándar de Beeznz Work.',
  },
  {
    num: '06',
    icon: TrendingUp,
    title: 'Toma decisiones basadas en datos',
    desc: 'Accede a reportes de desempeño, promedios por área y tendencias para identificar talento y áreas de oportunidad.',
  },
]

const PILLARS = [
  {
    icon: ShieldCheck,
    title: 'Reputación laboral verificable',
    desc: 'Evaluaciones, historiales y certificados que no se pueden falsificar. La confianza como base del ecosistema.',
    color: 'bg-emerald-50 text-emerald-600',
  },
  {
    icon: GraduationCap,
    title: 'Capacitación continua',
    desc: 'Cursos, talleres y certificaciones disponibles para que los colaboradores mejoren su empleabilidad constantemente.',
    color: 'bg-violet-50 text-violet-600',
  },
  {
    icon: TrendingUp,
    title: 'Indicadores reales de desempeño',
    desc: 'KPIs basados en datos objetivos que ayudan a empresas y colaboradores a tomar mejores decisiones profesionales.',
    color: 'bg-blue-50 text-blue-600',
  },
  {
    icon: FileText,
    title: 'Reportes laborales justos',
    desc: 'Un proceso transparente y revisado por Beeznz Work para reportar conductas graves de forma confiable y bilateral.',
    color: 'bg-amber-50 text-amber-600',
  },
]

function StepCard({
  num,
  icon: Icon,
  title,
  desc,
  last,
}: {
  num: string
  icon: React.ElementType
  title: string
  desc: string
  last?: boolean
}) {
  return (
    <div className="relative flex gap-5">
      {/* línea vertical */}
      {!last && (
        <div className="absolute left-5 top-12 h-full w-0.5 bg-slate-100" />
      )}
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-amber-400 z-10">
        <Icon size={18} />
      </div>
      <div className="pb-10">
        <p className="text-[10px] font-bold tracking-widest text-amber-500 uppercase">{num}</p>
        <h3 className="mt-0.5 text-base font-bold text-slate-900">{title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{desc}</p>
      </div>
    </div>
  )
}

function ComoFunciona() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="bg-slate-950 px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-300">
            Guía completa de la plataforma
          </span>
          <h1 className="mt-6 text-4xl font-bold leading-tight text-white sm:text-5xl">
            ¿Cómo funciona{' '}
            <span className="text-amber-400">Beeznz Work</span>?
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-300">
            Donde las buenas experiencias laborales se convierten en oportunidades. Empresas y colaboradores conectan
            con transparencia, confianza y crecimiento continuo.
          </p>
        </div>
      </section>

      {/* Pilares */}
      <section className="border-b border-slate-100 bg-white py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-10 text-center">
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Los cuatro pilares de Beeznz Work</h2>
            <p className="mt-3 text-slate-600">Mucho más que buscar empleo: una comunidad donde el talento, la confianza y las oportunidades crecen juntos.</p>
          </div>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map(({ icon: Icon, title, desc, color }) => (
              <div key={title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-md transition-shadow">
                <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
                  <Icon size={22} />
                </div>
                <h3 className="mt-4 font-semibold text-slate-900">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pasos */}
      <section className="bg-slate-50 py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-slate-900 sm:text-3xl">Paso a paso en la plataforma</h2>
            <p className="mt-3 text-slate-600">Un flujo pensado para colaboradores y empresas por igual.</p>
          </div>

          <div className="grid gap-12 lg:grid-cols-2">
            {/* Colaboradores */}
            <div className="rounded-3xl border border-slate-200 bg-white p-8">
              <div className="mb-8 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-amber-400">
                  <UserPlus size={20} />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Para colaboradores</span>
                  <h3 className="text-lg font-bold text-slate-900">Construye tu carrera</h3>
                </div>
              </div>
              <div>
                {STEPS_COLABORADOR.map((step, i) => (
                  <StepCard key={step.num} {...step} last={i === STEPS_COLABORADOR.length - 1} />
                ))}
              </div>
              <Link
                to="/registro"
                className="mt-2 inline-flex items-center gap-2 rounded-lg bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
              >
                Crear perfil de colaborador <ArrowRight size={16} />
              </Link>
            </div>

            {/* Empresas */}
            <div className="rounded-3xl border border-slate-200 bg-white p-8">
              <div className="mb-8 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-900 text-amber-400">
                  <Building2 size={20} />
                </div>
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Para empresas</span>
                  <h3 className="text-lg font-bold text-slate-900">Encuentra y desarrolla talento</h3>
                </div>
              </div>
              <div>
                {STEPS_EMPRESA.map((step, i) => (
                  <StepCard key={step.num} {...step} last={i === STEPS_EMPRESA.length - 1} />
                ))}
              </div>
              <Link
                to="/registro"
                className="mt-2 inline-flex items-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Registrar mi empresa <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reputación laboral */}
      <section className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <span className="text-xs font-bold uppercase tracking-wider text-amber-600">Ecosistema de confianza</span>
              <h2 className="mt-3 text-3xl font-bold text-slate-900">¿Qué es la reputación laboral verificable?</h2>
              <p className="mt-4 leading-relaxed text-slate-600">
                Creamos una nueva forma de demostrar quién eres profesionalmente. Un historial laboral construido
                con experiencias verificadas, evaluaciones reales y certificaciones obtenidas a través de tu
                crecimiento constante.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Evaluaciones de desempeño firmadas por empresas reales',
                  'Certificados digitales de capacitaciones completadas',
                  'Reportes laborales revisados y validados',
                  'Periodos de trabajo con fechas y roles verificables',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm text-slate-700">
                    <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-amber-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: 'Evaluaciones mensuales', value: '10 aspectos', icon: Star, color: 'text-amber-600', bg: 'bg-amber-50' },
                { label: 'Escala de calificación', value: '1 a 10 puntos', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'Certificados digitales', value: 'Por curso completado', icon: Award, color: 'text-violet-600', bg: 'bg-violet-50' },
                { label: 'Reportes validados', value: 'Revisión por Beeznz Work', icon: ShieldCheck, color: 'text-emerald-600', bg: 'bg-emerald-50' },
              ].map(({ label, value, icon: Icon, color, bg }) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 text-center">
                  <div className={`mx-auto flex h-10 w-10 items-center justify-center rounded-xl ${bg}`}>
                    <Icon size={20} className={color} />
                  </div>
                  <p className="mt-3 text-sm font-bold text-slate-900">{value}</p>
                  <p className="mt-0.5 text-xs text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ rápido */}
      <section className="bg-slate-50 py-16 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-8 text-center text-2xl font-bold text-slate-900">Preguntas frecuentes</h2>
          <div className="space-y-4">
            {[
              {
                q: '¿Es gratuito registrarse?',
                a: 'Sí. Crear una cuenta como colaborador o empresa en Beeznz Work es completamente gratuito. Algunas funcionalidades premium estarán disponibles en planes futuros.',
              },
              {
                q: '¿Cómo se validan los reportes laborales?',
                a: 'Cada reporte es revisado por el equipo de Beeznz Work antes de ser publicado en el historial. Solo los casos con evidencia suficiente y proceso de revisión completado se integran al perfil.',
              },
              {
                q: '¿Puede una empresa ver el historial completo de un colaborador?',
                a: 'Las empresas pueden ver el historial laboral verificable de colaboradores que se postulan a sus vacantes o con quienes tienen un vínculo activo en la plataforma.',
              },
              {
                q: '¿Qué pasa si termina una relación laboral?',
                a: 'El vínculo se marca como finalizado y el historial se conserva. Las evaluaciones registradas durante ese periodo siguen siendo visibles en el perfil del colaborador.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="rounded-xl border border-slate-200 bg-white p-5">
                <div className="flex items-start gap-3">
                  <ChevronRight size={16} className="mt-0.5 shrink-0 text-amber-500" />
                  <div>
                    <p className="font-semibold text-slate-900">{q}</p>
                    <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{a}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-slate-950 py-20 px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="text-3xl font-bold text-white sm:text-4xl">
            Empieza hoy en Beeznz Work
          </h2>
          <p className="mt-4 text-lg text-slate-300">
            Únete al ecosistema laboral que promueve transparencia, profesionalización y mejores oportunidades para todos.
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Link
              to="/registro"
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-amber-400 px-7 py-3 text-sm font-semibold text-slate-900 transition hover:bg-amber-300"
            >
              Crear cuenta gratis <ArrowRight size={18} />
            </Link>
            <Link
              to="/empleos"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-slate-600 px-7 py-3 text-sm font-semibold text-white transition hover:border-slate-400"
            >
              Ver vacantes disponibles
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ComoFunciona
